import { useCallback, useEffect, useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceTemplate, Sending } from 'src/modules/invoice/models/invoiceTemplate';
import { FormProvider, useForm } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getInvoiceTemplateById, updateInvoiceTemplate } from 'src/shared/services/invoiceService';
import * as yup from 'yup';
import { Box } from '@mui/material';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { yupResolver } from '@hookform/resolvers/yup';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { RenderInvoiceTemplateSending } from './renderInvoiceTemplateSending';
import { InvoiceTemplateInformation } from './invoiceTemplateInformation';

interface invTempPartialValues {
  sendings: Sending[];
}

type AdminInvoiceTemplateDetailProps = {
  openTemplateDetails: boolean;
  setOpenTemplateDetails: (onClose: boolean) => void;
  templateId: number;
  setTemplateId: (setTemplateId: number) => void;
  updateData: () => void;
};

export const AdminInvoiceTemplateDetail = (props: AdminInvoiceTemplateDetailProps) => {
  const { openTemplateDetails, setOpenTemplateDetails, templateId, setTemplateId, updateData } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [invoiceTemplate, setInvoiceTemplate] = useState<InvoiceTemplate>();

  const invoiceTemplateSchema = yup.object().shape({
    sendings: yup.array(
      yup.object().shape({
        defaultHeading: yup.string().optional(),
        defaultText: yup.string().optional(),
        expireDate: yup.string().optional(),
        emailHeading: yup.string().optional(),
        emailText: yup.string().optional(),
      }),
    ),
  });

  const formMethods = useForm<invTempPartialValues>({
    resolver: yupResolver(invoiceTemplateSchema),
    mode: 'all',
    defaultValues: {
      sendings: [],
    },
  });

  const { handleSubmit, reset } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getInvoiceTemplateById(templateId);
      if (isMounted()) {
        setInvoiceTemplate(result.data);
        reset(result.data);
      }
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, reset, templateId]);

  useEffect(() => {
    if (templateId) {
      fetchData();
    }
  }, [fetchData, templateId]);

  const onHandleClose = () => {
    setTemplateId(0);
    setOpenTemplateDetails(false);
  };

  const onSubmit = async (data: invTempPartialValues) => {
    setLoading(true);
    const dataToPost: InvoiceTemplate = {
      ...invoiceTemplate,
      template_id: invoiceTemplate?.template_id || 0,
      owner_id: invoiceTemplate?.owner_id || 0,
      approvePaymentsGreaterThanInvoiced: invoiceTemplate?.approvePaymentsGreaterThanInvoiced || false,
      name: invoiceTemplate?.name || '',
      sendings: data.sendings,
    };

    try {
      updateInvoiceTemplate(dataToPost);
      if (isMounted()) {
        updateData();
        onHandleClose();
        toast.success(<>{t('invoice:adminInvoiceTemplatePage.invTempUpdated')}</>);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log('Error occurred:', e);
        toast.error(<>{t('invoice:adminInvoiceTemplatePage.invTempUpdatedError')}</>);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const onHandleSave = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GenericDialog
          disableBackdropClosing
          open={openTemplateDetails}
          padding={0}
          disableEnforceFocus
          dialogTitle={invoiceTemplate?.name}
          onClose={onHandleClose}
          onSave={onHandleSave}
          dialogContent={
            <Box
              sx={{
                '& .MuiAccordionSummary-root': {
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(90deg)',
                  },
                  '& .MuiAccordionSummary-content': {
                    ml: 1,
                  },
                },
                '& .MuiAccordion-root.Mui-expanded': {
                  borderBottom: '1px solid #e0e0e0',
                  boxShadow: '0 20px 15px -20px rgba(0, 0, 0, 0.1) inset !important',
                  margin: 0,
                },
              }}
            >
              {invoiceTemplate && <InvoiceTemplateInformation invoiceTemplate={invoiceTemplate} />}
              {invoiceTemplate?.sendings &&
                invoiceTemplate.sendings.map((item, index) => {
                  if (item.sendingType === InvoiceSendingTypeIntegerEnum.Email) {
                    return null;
                  }

                  return (
                    <Box key={item.templateSendings_id}>
                      {item.customerEditEnabled && (
                        <RenderInvoiceTemplateSending loading={loading} sendingItem={item} index={index} setLoading={setLoading} />
                      )}
                    </Box>
                  );
                })}
            </Box>
          }
        />
      </form>
    </FormProvider>
  );
};
